import Api from "../api/api";
import { useEffect, useState, useRef } from "react";
import { DateGroupingTypeEnum, reportDatePresetByLabel } from "./reportsEnums";
import { dateRange, toDateString } from "../../utilities/dateUtilities";
import { saveAs } from 'file-saver';
import Loader from "../shared/loader";
import { EmptyState } from "../dashboard/emptyState";
import { ReportsCharts } from "./reportsCharts";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import { createGroupedChartTraces } from "./chartUtils";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterDisplay } from "../shared/appliedFilterDisplay";
import { ContraventionsReportFiltersModal } from "./filters-modals/contraventionsReportFiltersModal";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
import { ContraventionsScheduleModal } from "./schedule-modals/contraventionsScheduleModal";

export default function ContraventionsReport({ sites, organisationId, organisationName, organisationLogo, token }) {
    const [reportsData, setReportsData] = useState(null);
    const [fromDate, setFromDate] = useState(dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["Last month"]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSite, setSelectedSite] = useState(!arrayUtilities.isNullOrEmpty(sites) ? sites[0].id : null);
    const [selectedDateGrouping, setSelectedDateGrouping] = useState(0);
    const [chartData, setChartData] = useState(null);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const reportsLandingUrl = `/organisation/${ organisationId }/reports`;
    const pdfRef = useRef()

    const pdfSiteName = (siteId) => {
        if (!stringHelpers.isNullOrEmpty(siteId)) {
            const site = sites.find(item => item.id === siteId)
            if (site !== undefined) {
                return site.name
            }
        }
        return "All"
    }

    const pdfDateGroupingName = (dateGroupingValue) => {
        const dateGroupingName = DateGroupingTypeEnum[dateGroupingValue]
        return !stringHelpers.isNullOrEmpty(dateGroupingName) ? dateGroupingName : "All"
    }

    const fetchContraventionsReport = (from, to, dateGrouping, siteId) => {
        setIsLoading(true);
        Api.getContraventionsReport(
            reportData => {
                setReportsData(
                    reportData.map(item => (
                        {
                            label: item.label,
                            contraventionName: item.contraventionName,
                            contraventionType: item.contraventionType,
                            value: item.value,
                            shortLabel: item.shortLabel
                        }
                    ))
                )

                setIsLoading(false)
            },
            () => setIsLoading(false),
            from, to, 0, Number(dateGrouping), siteId, organisationId, token
        )
    }

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        const defaultSite = !arrayUtilities.isNullOrEmpty(sites) ? sites[0] : null;
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setSelectedSite(defaultSite ? defaultSite.id : null);
        setSelectedDateGrouping(0);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Site": defaultSite?.name,
            "Date Grouping": DateGroupingTypeEnum[0]
        });
        setShowFilterPills(true);
        fetchContraventionsReport(defaultRange.firstDate, defaultRange.lastDate, 0, defaultSite ? defaultSite.id : null);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Date Range": {
                const defaultRange = dateRange("lastMonth")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["Last month"])
                fetchContraventionsReport(defaultRange.firstDate, defaultRange.lastDate, selectedDateGrouping, selectedSite)
                break
            }
            case "Site": {
                const defaultSite = !arrayUtilities.isNullOrEmpty(sites) ? sites[0] : null
                setSelectedSite(defaultSite ? defaultSite.id : null)
                fetchContraventionsReport(fromDate, toDate, selectedDateGrouping, defaultSite ? defaultSite.id : null)
                break
            }
            case "Date Grouping":
                setSelectedDateGrouping(0)
                fetchContraventionsReport(fromDate, toDate, 0, selectedSite)
                break
            default:
                break
        }
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        siteId: selectedSite,
        dateRangeType: selectedDateGrouping,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchContraventionsReport(fromDate, toDate, selectedDateGrouping, selectedSite);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Site": !arrayUtilities.isNullOrEmpty(sites) ? sites.find(item => item.id === selectedSite)?.name : null,
            "Date Grouping": DateGroupingTypeEnum[selectedDateGrouping]
        });
        setShowFilterPills(true);
    }, [])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(reportsData)) {
            const contraventionsNameOptions = reportsData.reduce((acc, curr) => {
                if (!acc.includes(curr.contraventionName)) acc.push(curr.contraventionName);
                return acc;
            }, [])

            const valuesByDate = reportsData.reduce((acc, curr) => {
                acc[curr.shortLabel] = {
                    ...acc[curr.shortLabel],
                    [`${ curr.contraventionName }`]: curr.value
                }
                return acc
            }, {})

            setChartData(createGroupedChartTraces(contraventionsNameOptions, valuesByDate))
        }
    }, [reportsData])

    const handleDownload = (outputFormat) => {
        setIsLoading(true)
        Api.getContraventionsReport(
            (blob, filename) => { saveAs(blob, filename); setIsLoading(false) },
            () => { setIsLoading(false) },
            fromDate, toDate, outputFormat, Number(selectedDateGrouping), selectedSite, organisationId, token
        )
    }

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            Contraventions
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                            <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                        </div>
                                        <button
                                                onClick={ () => handleDownload(1) }
                                                className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                            >
                                                Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                            </button>
                                            <button
                                                onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - Contraventions`) }
                                                className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                            >
                                                Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                            </button>
                                            <button
                                                onClick={ () => setShowScheduleExportModal(true) }
                                                className="btn btn-sm btn-secondary nowrap mr-2"
                                            >
                                                Schedule Report<i className="ml-2 far fa-clock"></i>
                                            </button>
                                            <Link
                                                className="btn btn-falcon-default btn-sm mr-3"
                                                type="button"
                                                to={ reportsLandingUrl }>
                                                <i className="fas fa-reply"></i> Back
                                            </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => {
                                                    return !stringHelpers.isNullOrEmpty(value)
                                                        && <div className="mr-2" key={ label }>
                                                            <AppliedFilterDisplay
                                                                label={ label }
                                                                isResettable={ true }
                                                                onTextClick={ () => setShowFiltersModal(true) }
                                                                onCloseClick={ closedLabel => {
                                                                    clearFilterByLabel(closedLabel)
                                                                    setAppliedFilters(prevState => {
                                                                        const updatedFilters = { ...prevState }
                                                                        if (closedLabel === "Date Range") {
                                                                            const defaultRange = dateRange("lastMonth")
                                                                            updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
                                                                        } else if (closedLabel === "Site") {
                                                                            const defaultSite = !arrayUtilities.isNullOrEmpty(sites) ? sites[0] : null
                                                                            updatedFilters["Site"] = defaultSite?.name
                                                                        } else if (closedLabel === "Date Grouping") {
                                                                            updatedFilters["Date Grouping"] = DateGroupingTypeEnum[0]
                                                                        }
                                                                        const hasRemainingFilters = Object.values(updatedFilters).some(value => !stringHelpers.isNullOrEmpty(value))
                                                                        return hasRemainingFilters ? updatedFilters : null
                                                                    })
                                                                } }
                                                                value={ value }
                                                            />
                                                        </div>
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }

                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Contraventions">
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Range</span>
                                            <span className="print-only-filter-value">
                                                { toDateString(fromDate) } - { toDateString(toDate) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Site</span>
                                            <span className="print-only-filter-value">
                                                { pdfSiteName(selectedSite) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Grouping</span>
                                            <span className="print-only-filter-value">
                                                { pdfDateGroupingName(selectedDateGrouping) }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                    {
                                        isLoading
                                            ? <Loader />
                                            : <>
                                                {
                                                    !arrayUtilities.isNullOrEmpty(reportsData) ? (<div className="mt-3 d-flex flex-column">
                                                        { chartData && <ReportsCharts isGroupedChart chartStyle="bar" data={ chartData } showLegend />
                                                        }
                                                        <div className="card-body bg-white border-top p-0">
                                                            <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                                <thead className="bg-200 text-900">
                                                                    <tr>
                                                                        <th>Date</th>
                                                                        <th>Contravention Name</th>
                                                                        <th>Contravention Type</th>
                                                                        <th className="text-right">Number of Vehicles</th>
                                                                    </tr>
                                                                </thead>

                                                                <tbody>
                                                                    {
                                                                        !arrayUtilities.isNullOrEmpty(reportsData)
                                                                        && reportsData.map((item, index) =>
                                                                            <tr key={ index }>
                                                                                <td>{ item.label }</td>
                                                                                <td>{ item.contraventionName }</td>
                                                                                <td>{ item.contraventionType }</td>
                                                                                <td className="text-right">{ stringHelpers.formatDecimal(item.value, 0) }</td>
                                                                            </tr>
                                                                        )
                                                                    }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>) : <EmptyState message="No Data available for the selected filters" /> }
                                            </> }
                                </div>
                                {
                                    showScheduleExportModal &&
                                    <ContraventionsScheduleModal
                                        sites={ sites }
                                        organisationId={ organisationId }
                                        token={ token }
                                        initialParameters={ scheduleInitialParameters }
                                        onCancel={ closeScheduleExportModal }
                                        onSaved={ closeScheduleExportModal }
                                    />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <ContraventionsReportFiltersModal
                    showModal={ showFiltersModal }
                    handleClose={ () => setShowFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setSelectedSite(draftFilters.selectedSite)
                        setSelectedDateGrouping(draftFilters.selectedDateGrouping)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchContraventionsReport(draftFilters.fromDate, draftFilters.toDate, draftFilters.selectedDateGrouping, draftFilters.selectedSite)
                    } }
                    initialFromDate={ fromDate }
                    initialToDate={ toDate }
                    initialDatePreset={ datePreset }
                    initialSelectedSite={ selectedSite }
                    initialSelectedDateGrouping={ selectedDateGrouping }
                    initialAppliedFilters={ appliedFilters }
                    sites={ sites }
                    key="contraventions-report-filters-modal"
                />
            }
        </>
    )
}
